/**
 * AdminVerifierWorkPage — everything one verifier is carrying.
 *
 * The admin counterpart to /verifier/my-work: that page is the verifier's own
 * queue, this is the admin's view of a named verifier's load, reached from the
 * "Assigned to <name>" link on a verification and from their user record.
 *
 * Reads GET /api/admin/users/:userId/verifications, which merges the three
 * flows that carry a verifier_id. Merging is right here in a way it is not on
 * the Verifications page: the question is "what is this person carrying",
 * which is one list — `source` keeps the differences visible.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Chip, Alert, Skeleton, Button, Link,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { ArrowBack, Assignment, HomeWork, Engineering } from '@mui/icons-material';

import adminService from '../services/adminService';
import adminApplicationsService from '../services/adminApplicationsService';
import ApplicationActions from '../components/admin/ApplicationActions';
import { panelSx, panelHeaderSx, tableSx, tableRowSx } from '../components/admin/surfaces';
import { brand, status as statusTokens, text } from '../theme/colors';
import { formatCurrency } from '../utils/helpers';

// Roles whose application can be approved from this page (gated verifier roles).
const VERIFIER_ROLES = ['verifier', 'professional_verifier', 'quality_inspector'];

const EMPTY = '—';
const titleise = (v) => String(v || '').replace(/_/g, ' ');

const formatDate = (value) => {
  if (!value) return EMPTY;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

/** The three flows a verifier works across. */
const SOURCES = {
  project: { label: 'Project', icon: <Assignment sx={{ fontSize: 15 }} />, tone: statusTokens.info },
  property: { label: 'Property check', icon: <HomeWork sx={{ fontSize: 15 }} />, tone: brand.primary },
  contractor: { label: 'Contractor vetting', icon: <Engineering sx={{ fontSize: 15 }} />, tone: statusTokens.warning },
};

const STATUS_TONES = {
  pending: statusTokens.warning,
  assigned: statusTokens.info,
  accepted: statusTokens.info,
  in_progress: statusTokens.info,
  submitted: statusTokens.warning,
  changes_requested: statusTokens.warning,
  verification_completed: statusTokens.success,
  completed: statusTokens.success,
  approved: statusTokens.success,
  rejected: statusTokens.error,
  disputed: statusTokens.error,
  cancelled: statusTokens.neutral,
};

const RISK_TONES = {
  low: statusTokens.successDark,
  standard: statusTokens.infoDark,
  elevated: statusTokens.warningDark,
  high: statusTokens.errorDark,
};

export default function AdminVerifierWorkPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [work, setWork] = useState(null);
  const [person, setPerson] = useState(null);
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  // `quiet` refreshes in place (after an approve/reject) instead of swapping the
  // page for the skeleton, which would unmount the actions and drop their
  // confirmation message.
  const load = useCallback(async ({ isCancelled = () => false, quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      // The person's name is nice-to-have: the work list stands on its own.
      const [w, u, appRes] = await Promise.all([
        adminService.getUserVerificationWork(userId),
        adminService.getUserById(userId).catch(() => null),
        // Verifiers are a gated role, so they have an application record that
        // carries the approve/reject controls. Supplementary — never fatal.
        adminApplicationsService.detail(userId).catch(() => null),
      ]);
      if (isCancelled()) return;
      setWork(w);
      setPerson(u);
      setApplication(appRes?.data || null);
      setError('');
    } catch (e) {
      if (isCancelled()) return;
      setError(e?.response?.data?.error || e?.message || 'Failed to load assigned verifications');
    } finally {
      if (!isCancelled() && !quiet) setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    load({ isCancelled: () => cancelled });
    return () => { cancelled = true; };
  }, [load]);

  const counts = work?.counts;
  const allRows = work?.rows || [];
  const rows = filter === 'all'
    ? allRows
    : filter === 'open'
      ? allRows.filter((r) => !['completed', 'approved', 'cancelled', 'rejected'].includes(String(r.status)))
      : allRows.filter((r) => r.source === filter);

  const name = person?.profile
    ? `${person.profile.firstName || ''} ${person.profile.lastName || ''}`.trim() || person.account?.email
    : person?.account?.email;

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(`/admin/home/users/${userId}`, {
          state: { listSearch: location.search, listPath: location.pathname },
        })}
        sx={{ textTransform: 'none', color: text.secondary, mb: 2 }}
      >
        Back to user record
      </Button>

      <Typography sx={{ fontSize: 22, fontWeight: 700, color: text.primary, mb: 0.25 }}>
        Assigned verifications
      </Typography>
      <Typography variant="body2" sx={{ color: text.secondary, mb: 2.5 }}>
        {loading
          ? 'Loading…'
          : `${name || 'This verifier'} · ${counts?.total || 0} total, ${counts?.open || 0} still open`}
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2.5, borderRadius: 2 }}
          action={<Button color="inherit" size="small" onClick={() => load()}>Retry</Button>}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Skeleton variant="rounded" height={320} sx={{ borderRadius: 2 }} />
      ) : (
        <>
          {/* Application decision — approve/reject this verifier's application,
              the same controls as /admin/applications, so an admin reviewing
              the verifier's workload can act without leaving the page. */}
          {application && VERIFIER_ROLES.includes(application.role) && (
            <Box sx={{ ...panelSx, p: 2.5, mb: 2.5 }}>
              <Typography sx={{ fontWeight: 700, color: text.primary, fontSize: 15, mb: 0.5 }}>
                Application decision
              </Typography>
              <Typography variant="body2" sx={{ color: text.secondary, mb: 1.75 }}>
                Approving grants this verifier full access and emails them. Approval is
                blocked until every mandatory requirement is satisfied or waived.
              </Typography>
              <ApplicationActions application={application} onChanged={() => load({ quiet: true })} />
            </Box>
          )}

          <ToggleButtonGroup
            size="small"
            exclusive
            value={filter}
            onChange={(_e, v) => v && setFilter(v)}
            sx={{ mb: 2.5, flexWrap: 'wrap' }}
          >
            <ToggleButton value="all" sx={{ textTransform: 'none' }}>
              All ({counts?.total || 0})
            </ToggleButton>
            <ToggleButton value="open" sx={{ textTransform: 'none' }}>
              Open ({counts?.open || 0})
            </ToggleButton>
            {Object.entries(SOURCES).map(([key, cfg]) => (
              <ToggleButton key={key} value={key} sx={{ textTransform: 'none' }}>
                {cfg.label} ({counts?.bySource?.[key] || 0})
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Paper elevation={0} sx={panelSx}>
            <Box sx={panelHeaderSx}>
              <Typography sx={{ fontWeight: 600, fontSize: 14, color: text.primary }}>
                {rows.length} {rows.length === 1 ? 'verification' : 'verifications'}
              </Typography>
            </Box>

            <TableContainer>
              <Table size="medium" sx={tableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Fee</TableCell>
                    <TableCell>Due</TableCell>
                    <TableCell>Assigned</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 6, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: text.tertiary }}>
                          {allRows.length === 0
                            ? 'Nothing is assigned to this verifier.'
                            : 'Nothing matches this filter.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : rows.map((r) => {
                    const src = SOURCES[r.source] || {};
                    const tone = STATUS_TONES[r.status] || statusTokens.neutral;
                    return (
                      <TableRow key={`${r.source}-${r.id}`} sx={tableRowSx}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Chip
                            size="small"
                            icon={src.icon}
                            label={src.label || titleise(r.source)}
                            sx={{
                              fontSize: 11, height: 22, fontWeight: 600,
                              color: src.tone, bgcolor: `${src.tone}1a`,
                              '& .MuiChip-icon': { color: src.tone },
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          {/* Contractor vetting has its own admin page; the
                              other two flows have no per-item admin screen. */}
                          {r.source === 'contractor' && r.verificationId ? (
                            <Link
                              component="button"
                              type="button"
                              underline="hover"
                              onClick={() => navigate(`/admin/home/verifications/contractor/${r.verificationId}`)}
                              sx={{
                                color: text.primary, fontWeight: 500, fontSize: 'inherit',
                                fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer',
                                '&:hover': { color: brand.primary },
                              }}
                            >
                              {r.title}
                            </Link>
                          ) : (
                            <Typography sx={{ fontSize: 14, color: text.primary, fontWeight: 500 }}>
                              {r.title}
                            </Typography>
                          )}
                          {(r.subtitle || r.riskLevel) && (
                            <Typography variant="caption" sx={{ color: text.tertiary, display: 'block' }}>
                              {r.subtitle}
                              {r.riskLevel && (
                                <Box component="span" sx={{ color: RISK_TONES[r.riskLevel], fontWeight: 600 }}>
                                  {r.subtitle ? ' · ' : ''}{titleise(r.riskLevel)} risk
                                </Box>
                              )}
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Chip
                            size="small"
                            label={titleise(r.status)}
                            sx={{
                              textTransform: 'capitalize', fontWeight: 600, fontSize: 11,
                              height: 22, color: tone, bgcolor: `${tone}1a`,
                            }}
                          />
                        </TableCell>

                        <TableCell sx={{ color: text.secondary, whiteSpace: 'nowrap' }}>
                          {r.fee != null ? formatCurrency(r.fee) : EMPTY}
                        </TableCell>
                        <TableCell sx={{ color: text.secondary, whiteSpace: 'nowrap' }}>
                          {formatDate(r.dueAt)}
                        </TableCell>
                        <TableCell sx={{ color: text.secondary, whiteSpace: 'nowrap' }}>
                          {formatDate(r.createdAt)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
}
